<script setup lang="ts">
  import { CustomModal } from '../CustomModal'
  import { UIInput, UIContentWrap, UIFormItem } from '@gohighlevel/ghl-ui'
  import { InputWithCustomValues } from '../../InputWithCustomValues'
  import { ref, computed, watch, PropType } from 'vue'
  import { useVueI18n } from '../../../locales'
  import { ModalEvents } from '../../../types/components'
  import { Role } from '../../../types/common/common'

  const locale = computed(() => {
    return window.localStorage.getItem('locale') || 'en-US'
  })
  const t = useVueI18n()

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    // When provided the modal acts as an "edit role" form.
    role: {
      type: Object as PropType<Role | null>,
      default: null,
    },
    // Names of other roles already defined on the template (excluding the one being edited),
    // used to validate that the entered role name is unique.
    existingRoleNames: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  })

  const roleName = ref('')
  const firstName = ref('')
  const lastName = ref('')
  const email = ref('')
  const phoneNumber = ref('')

  const emits = defineEmits([ModalEvents.ON_SUCCESS, ModalEvents.ON_CANCEL])

  const setFormFromRole = (role: Role | null) => {
    roleName.value = role?.name || ''
    firstName.value = role?.firstName || ''
    lastName.value = role?.lastName || ''
    email.value = role?.email || ''
    phoneNumber.value = role?.phoneNumber || ''
  }

  watch(
    () => props.show,
    show => {
      if (show) setFormFromRole(props.role)
    }
  )

  const onCancelForm = () => {
    emits(ModalEvents.ON_CANCEL)
  }

  // The form is reset from props whenever the modal is (re)opened, so we don't clear
  // on success — this keeps the entered data intact if the parent rejects it (e.g. a
  // duplicate role) and leaves the modal open.
  const onSuccessForm = () => {
    // Strip all whitespace from a real phone number (keeps custom-value tokens as-is) so the
    // stored value is clean, e.g. "+1 123 456 7890" -> "+11234567890".
    const phone = phoneNumber.value.trim()
    const cleanedPhone = containsToken(phone) ? phone : phone.replace(/\s+/g, '')
    emits(ModalEvents.ON_SUCCESS, {
      name: roleName.value.trim(),
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      email: email.value.trim(),
      phoneNumber: cleanedPhone,
    } as Role)
  }

  // A value that contains a {{ ... }} custom-value token is resolved at send time, so its
  // format can't be validated up front — we skip the format check for those.
  const containsToken = (val: string) => /\{\{.*\}\}/.test(val)

  // {{ contact.email }} resolves to the primary recipient's email, which is not allowed for a
  // role — treat it as an invalid email.
  const isContactEmailToken = (val: string) => /\{\{\s*contact\.email\s*\}\}/i.test(val)

  const isValidEmail = computed(() => {
    const val = email.value.trim()
    if (!val) return false
    if (isContactEmailToken(val)) return false
    if (containsToken(val)) return true
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
  })

  // Phone is optional. When present (and not a custom value) it must be a generic phone number:
  // digits with optional +, spaces, dashes, parentheses — no letters.
  const isValidPhone = computed(() => {
    const val = phoneNumber.value.trim()
    if (!val) return true
    if (containsToken(val)) return true
    return /^\+?[0-9\s\-()]+$/.test(val)
  })

  // A role name must be unique on the template (case-insensitive).
  const isDuplicateRoleName = computed(() => {
    const val = roleName.value.trim().toLowerCase()
    if (!val) return false
    return props.existingRoleNames.some(name => name.trim().toLowerCase() === val)
  })

  // Error messages are shown below the field (only once the user has typed something) to match
  // the rest of our forms.
  const roleNameError = computed(() => (isDuplicateRoleName.value ? t('common.roleAlreadyExists') : ''))
  const emailError = computed(() => (email.value.trim() && !isValidEmail.value ? t('common.invalidEmail') : ''))
  const phoneError = computed(() => (phoneNumber.value.trim() && !isValidPhone.value ? t('common.invalidPhone') : ''))

  const modalDisabled = computed(() => {
    return (
      !roleName.value.trim() ||
      !firstName.value.trim() ||
      !lastName.value.trim() ||
      isDuplicateRoleName.value ||
      !isValidEmail.value ||
      !isValidPhone.value
    )
  })
</script>

<template>
  <CustomModal
    :width="620"
    :show="props.show"
    :modalType="'primary'"
    :title="props.role ? t('common.editRole') : t('common.addRole')"
    @on-cancel="onCancelForm"
    @on-success="onSuccessForm"
    id="add-role-modal"
    :cancelActionText="t('common.cancel')"
    :successActionText="props.role ? t('common.save') : t('common.add')"
    :headerIcon="false"
    :disabled="modalDisabled"
    :closeOnSuccess="false"
  >
    <UIContentWrap :locale="(locale?.value as any) || 'en-US'" class="p-0" style="display: contents">
      <div class="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-500 mb-5">
        {{ t('common.addRoleNote') }}
      </div>

      <div class="mb-5">
        <UIFormItem
          :label="t('common.roleName')"
          :show-feedback="!!roleNameError"
          :validation-status="roleNameError ? 'error' : undefined"
          :feedback="roleNameError || undefined"
        >
          <UIInput id="role-name" v-model:value="roleName" :placeholder="t('common.roleName')" />
        </UIFormItem>
      </div>

      <div class="mb-5">
        <InputWithCustomValues
          id="role-first-name"
          :label="t('common.firstName')"
          :value="firstName"
          :required="true"
          :show-feedback="false"
          @update:value="(val: string) => (firstName = val)"
        />
      </div>

      <div class="mb-5">
        <InputWithCustomValues
          id="role-last-name"
          :label="t('common.lastName')"
          :value="lastName"
          :required="true"
          :show-feedback="false"
          @update:value="(val: string) => (lastName = val)"
        />
      </div>

      <div class="mb-5">
        <InputWithCustomValues
          id="role-email"
          :label="t('common.email')"
          :value="email"
          :required="true"
          :show-feedback="false"
          :error="emailError"
          @update:value="(val: string) => (email = val)"
        />
      </div>

      <div class="mb-2">
        <InputWithCustomValues
          id="role-phone-number"
          :label="t('common.phoneNumber')"
          :value="phoneNumber"
          :placeholder="t('common.phoneNumberPlaceholder')"
          :show-feedback="false"
          :error="phoneError"
          @update:value="(val: string) => (phoneNumber = val)"
        />
      </div>
    </UIContentWrap>
  </CustomModal>
</template>
